import { DynamicUGOHub } from './DynamicUGOHub';
import { DynamicProfileStoreBase } from '../DynamicProfileStoreBase';
import { ProfileKeyCode, SketchState } from '../DynamicProfileEnums';
import { reachLineSymbol, channelLineSymbol, reachStationSymbol } from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import UgoInlet from '../../../../../src/assets/images/ugo-inlet.svg';
import UgoOutlet from '../../../../../src/assets/images/ugo-outlet.svg';
import UgoJunction from '../../../../../src/assets/images/ugo-junction.svg';
import UgoGradeBreak from '../../../../../src/assets/images/ugo-elbow-gradebreak.svg';
import Stores from '../..';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { doEvents } from 'cd-eft/utils/sysUtils';
import { putStructureRepOnTop } from 'cd-eft/utils/gisUtils';
import { UgoDesignToolsController, ProfileController } from 'cd-eft/services/eftSetApi';
import { DynamicProfileClass } from '../DynamicProfileClass';
import { AppConstants } from 'cd-eft/appConstants';
import { IUgoVertexParamsDTO } from '../../UGODesignStore';
import { formatStationString, parseStationString } from 'cd-eft/utils/stationUtilities';
import { esriGeometryEngine, FeatureLayer, Graphic, GraphicsLayer, MapView, Point, Polyline, SketchViewModel } from 'cd-eft/utils/esriTypes';
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import Multipoint from '@arcgis/core/geometry/Multipoint';

const { designStore, ugoDesignStore } = Stores.AppStore;

const ERROR_SELF_CROSSING = 'Common_29';

class RubberbandSketchViewModel {
  private _rubberbandSketchLayer: GraphicsLayer | undefined = void 0;
  private startPoint: Point | undefined = void 0;
  public _profileView: MapView | undefined = void 0;
  private viewPointerMoveHandle;
  private viewImmediateClickHandle;

  constructor(mapView: MapView, sketchLayer: GraphicsLayer) {
    this._profileView = mapView;
    this._rubberbandSketchLayer = sketchLayer;
  }

  public create(startPoint?: Point) {
    this.startPoint = startPoint;
    this.viewImmediateClickHandle = this._profileView!.on('immediate-click', this.viewImmediateClickHandler);
    this.viewPointerMoveHandle = this._profileView!.on('pointer-move', this.viewPointerMoveHandler);
  }

  public complete() {
    this.viewPointerMoveHandle?.remove();
    this.viewImmediateClickHandle?.remove();
    this.rubberbandSketchLayer.removeAll();
  }

  protected get rubberbandSketchLayer() {
    return this._rubberbandSketchLayer!;
  }

  private viewPointerMoveHandler = async (event: __esri.ViewPointerMoveEvent) => {
    this.rubberbandSketchLayer.removeAll();
    const movingPoint = this._profileView?.toMap(event);

    if (this.startPoint && movingPoint) {
      const rubberBandLine = new Polyline({ spatialReference: this._profileView!.spatialReference, hasZ: true });
      rubberBandLine.addPath([this.startPoint, movingPoint]);

      const rubberBandGraphic = new Graphic({ geometry: rubberBandLine, symbol: reachLineSymbol.designLineSymbol });
      this.rubberbandSketchLayer.add(rubberBandGraphic);
    }
  };

  private viewImmediateClickHandler = async (event: __esri.ViewImmediateClickEvent) => {
    this.complete();
  };
}
class DynamicUGOProfileStore extends DynamicProfileStoreBase {
  public hub: DynamicUGOHub;
  private addUgoEdits: any;
  private isAfterReshapeVertexAdd = false;
  private rubberbandSketchVm: RubberbandSketchViewModel | undefined;
  private extendSketchVM: SketchViewModel | undefined = void 0;
  protected _crosssectionLayer: GraphicsLayer | undefined;
  protected _ugoInletLayer: GraphicsLayer | undefined = void 0;
  protected _ugoOutletLayer: GraphicsLayer | undefined = void 0;
  protected _ugoJunctionLayer: GraphicsLayer | undefined = void 0;
  protected _ugoGradeBreakLayer: GraphicsLayer | undefined = void 0;
  private terraceProfileData: any;

  /** Add Fixture setup **/
  private ugoProfileClickHandler;
  private ugoProfileMoveHandler;
  private channelIndexes: any[] = [];
  private pipeVertices = [] as number[][];
  /**
   *
   */
  constructor(hub: DynamicUGOHub, addUgoEdits?) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.TGS, ProfileKeyCode.REACH];
  }

  protected setUpTerraceAlignmentView(event: any): void { }

  protected initSketchVm(): void {
    if (this.mainSketchVm && this.sketchLayer) {
      this.mainSketchVm.polylineSymbol = channelLineSymbol.designLineSymbol;
      this.mainSketchVm.view = this._profileView!;
      this.mainSketchVm.layer = this.sketchLayer;
      this.mainSketchVm.updateOnGraphicClick = false;
      this.mainSketchVm.defaultUpdateOptions = {
        toggleToolOnClick: false,
      };
    }
  }

  public removeSketch(): void {
    this.removeReachStationLayer();
    this.sketchLayer?.graphics.removeAll();
  }

  private get crosssectionLayer() {
    const id = 'earthwork_designs';
    const title = 'Earthwork Designs';

    if (!this._crosssectionLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._crosssectionLayer = layer as GraphicsLayer;
      } else {
        this._crosssectionLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._crosssectionLayer, 1);
      }
    }
    return this._crosssectionLayer;
  }

  protected async handlePolylineCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    if (event.state === 'start' || event.state === 'active') {
      if (event.toolEventInfo.type === 'vertex-add') {
        let x = event.toolEventInfo.vertices[0].coordinates[0];
        x = designStore.commonHorizontalStretchFactor ? x / designStore.commonHorizontalStretchFactor : x;
        const y = event.toolEventInfo.vertices[0].coordinates[1];

        if (this._isValidLine) {
          const line = event.graphic.geometry as Polyline;
          if (this.isValidLastSegment(line)) {
            this.clearInvalidState(event.graphic);
            this.pipeVertices.push([x, y]);
          } else {
            event.graphic.symbol = channelLineSymbol.invalidLineSymbol;
            MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
          }
        } else {
          // This is for the case of "slow" twice clicks
          this.mainSketchVm?.complete();
          const length = (event.graphic.geometry as Polyline).paths[0].length;
          await this.deleteReachVertex({ index: length - 1 });
          event.graphic.symbol = channelLineSymbol.designLineSymbol;
          this._isValidLine = true;
        }
      } else if (event.toolEventInfo.type === 'cursor-update') {
        if (!this._isValidLine) {
          this.mainSketchVm?.undo();
          this.clearInvalidState(event.graphic, 1500);
        }
      }
    }

    if (event.state === 'complete') {
      if (!this._isValidLine) {
        const length = (event.graphic.geometry as Polyline).paths[0].length;
        await this.deleteReachVertex({ index: length - 1 });
        this.clearInvalidState(void 0, 1500);
      }
      const isSuccess = await this.createDefaultUgoChannel(this.pipeVertices);
      if (isSuccess) {
        // terraceDesignStore.summaryInfo.channelLength = 1;
        // await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.ChannelDesignSave.string });
      }
      this.broadcastSketchState({ state: SketchState.COMPLETE, keyCode: this.profileKeys[1] });
      this.pipeVertices.length = 0;
    }

    if (event.state === 'cancel') {
      this.broadcastSketchState({ state: SketchState.CANCEL, keyCode: this.profileKeys[1] });
      this.pipeVertices.length = 0;
    }
  }

  async handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void> {
    if (event.toolEventInfo) {
      if (event.toolEventInfo.type.includes('reshape-stop')) {
        const line = event.graphics[0].geometry as Polyline;
        if (this.isValidFromStart(line)) {
          this.clearInvalidState(event.graphics[0]);

          const toolEventInfo = event.toolEventInfo as __esri.MoveEventInfo;
          if (toolEventInfo) {
            const moverAttr = toolEventInfo.mover.attributes;
            const movedPoint: Point = toolEventInfo.mover.geometry as Point;

            //get profile x and y values
            const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
            const yScale = designStore.commonVerticalStretchFactor
              ? designStore.commonVerticalStretchFactor
              : AppConstants.profileStretchFactor;
            const dataX = (movedPoint as Point).x / xScale;
            const dataY = (movedPoint as Point).y / yScale;
            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);

            if (moverAttr.pointIndex >= 0) {
              this.broadcastMoveVertexInfo(newVertex, moverAttr.pointIndex, this.addUgoEdits);
            }
          }
        } else {
          event.graphics[0].symbol = reachLineSymbol.invalidLineSymbol;
          await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);

          // clear error state
          setTimeout(() => {
            this.mainSketchVm?.undo();
            event.graphics[0].symbol = reachLineSymbol.designLineSymbol;
            this._isValidLine = true;
          }, 500);

          setTimeout(() => {
            MessageCenter.clearMessages();
          }, 1500);
        }
      } else if (event.toolEventInfo.type.includes('move-start')) {
      } else if (event.toolEventInfo.type.includes('move')) {
        this.mainSketchVm?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-remove')) {
        this.mainSketchVm?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-add')) {
        this.mainSketchVm?.undo();
        this.isAfterReshapeVertexAdd = true;
      } else if (event.toolEventInfo.type.includes('reshape-start')) {
      } else if (event.toolEventInfo.type.includes('reshape')) {
        if (this.isAfterReshapeVertexAdd) {
          this.mainSketchVm?.undo();
          this.isAfterReshapeVertexAdd = false;
        }
      }
    }
  }

  async createDefaultUgoChannel(channelData?) {
    designStore.setLoader('Generating channel reaches...');
    let isSuccess = false;
    const stationVertices = {};
    channelData.forEach((point) => {
      stationVertices[point[0]] =
        point[1] / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
    });

    try {
      const response = (
        await UgoDesignToolsController.createUgoDefaultReachesByAlignedElementDesignParamsIdAndStationsUsingPOST({
          alignedElementDesignParamsId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
          stations: stationVertices,
        })
      ).data as any;
      if (response && response.ugoVertexDTOList) {
        this.broadcastNewVertexInfo(response.ugoVertexDTOList);
        isSuccess = true;
      }
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError(
          (error as any).response.data.errorMessage,
          [],
          (error as any).response.data.originalErrorCode
        );
      }
    } finally {
      designStore.setLoader('');
    }
    return isSuccess;
  }

  SketchPoint(propAddEdits?: any) {
    this.addUgoEdits = propAddEdits;
    this.clearDelete();
    if (this.mainSketchVm) {
      this.mainSketchVm!.snappingOptions = {
        distance: 21,
        enabled: true,
        featureSources: [{ layer: this.sketchLayer }] as any,
      } as any;
    }

    this.mainSketchVm?.create('point');
    this.generateReachStationLayer(true);
    this.changePointSymbol('editableReachStation');
  }

  sketchPolyline() {
    this.clearDelete();
    this.removeSketch();
    this.resetFixtureLayers();
    this.removeMidPointStationLayer();
    this.broadcastSketchState({ state: SketchState.START, keyCode: this.profileKeys[1] });
    this.mainSketchVm?.create('polyline');
    this.setSketchLayer();
  }

  async handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    if (event.state === 'complete' && event.graphic && event.graphic.geometry) {
      const insertPoint = event.graphic.geometry as Point;
      const sr = event.graphic.geometry.spatialReference;

      if (this.sketchLayer?.graphics && this.sketchLayer.graphics.length > 1) {
        const channel = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
        // Check if the polyline contains the point
        if (!esriGeometryEngine.contains(channel, insertPoint)) {
          // If the polyline doesn't contain the point, display the error message
          await MessageCenter.addErrorFromCode('WDT_104');
        }
        const path = channel?.paths[0];

        const [startPoint, stopPoint] = this.getStartAndStopPoint(channel);

        if (esriGeometryEngine.distance(insertPoint, startPoint) === 0) {
          this.mainSketchVm?.create('point');
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (esriGeometryEngine.distance(insertPoint, stopPoint) === 0) {
          this.extendSketchVM?.create('point');
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (this.isSnappedOnVertex(insertPoint, channel)) {
          // do nothing
        } else {
          if (path) {
            let pointIndex = -1;
            let beginPoint = path[0];
            for (let index = 1; index < path.length; index++) {
              const endPoint = path[index];
              const polyline = new Polyline({ paths: [[beginPoint, endPoint]], spatialReference: sr });
              if (esriGeometryEngine.intersects(polyline, insertPoint)) {
                pointIndex = index;
                break;
              }
              beginPoint = endPoint;
            }
            // prepare for profile view
            const dataX = (insertPoint as Point).x / (designStore.commonHorizontalStretchFactor || 1);
            const dataY =
              (insertPoint as Point).y / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);
            if (pointIndex >= 0) {
              if (path[0][0] > path[path.length - 1][0]) {
                //digitized right to left, correct the order index for row.
                const rowIndex = path.length - pointIndex;
                channel.insertPoint(0, pointIndex, insertPoint);
                this.broadcastInsertVertexInfo(newVertex, rowIndex, this.profileKeys[0], this.addUgoEdits);
              } else {
                channel.insertPoint(0, pointIndex, insertPoint);
                this.broadcastInsertVertexInfo(newVertex, pointIndex, this.profileKeys[0], this.addUgoEdits);
              }
            }
          }
        }
      }

      if (this.mainSketchVm) this.mainSketchVm!.snappingOptions = {} as any;
      this.sketchLayer?.graphics.pop();
    } else if (event.state === 'complete' || event.state === 'cancel') {
      this.changePointSymbol('reachStation');
      if (this.mainSketchVm) this.mainSketchVm!.snappingOptions = {} as any;
    }
  }

  async generateUgoProfiles(sampleInterval: number) {
    this.profileKeys.forEach((keyCode) => {
      this.reset(keyCode);
    });
    this.resetProfileStructures();

    designStore.commonHorizontalStretchFactor = undefined;
    designStore.commonVerticalStretchFactor = undefined;

    try {
      this.raiseStartLengthyProcessEvent(true);
      await doEvents();

      const profiles = await UgoDesignToolsController.calculateUgoTerraceCrossSectionProfileUsingGET(
        ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
        sampleInterval
      );
      if (profiles) {
        // Get ymax and ymin
        const groundSurfaceProfileStations = profiles.data.groundProfile?.stationDTOList;
        let yValues;
        if (groundSurfaceProfileStations?.length) {
          yValues = await this.getMaxMinYValues([groundSurfaceProfileStations], this.alignmentCenterLine);
          this._centerlineStations = designStore.alignmentProfileStations = [...groundSurfaceProfileStations];

          // Draw ground surface
          const groundSurfaceProfile = this.profiles[ProfileKeyCode[10]] as DynamicProfileClass;
          groundSurfaceProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
          groundSurfaceProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
          groundSurfaceProfile.mapGroundProfilePoints(groundSurfaceProfileStations);
          const totalDist = groundSurfaceProfileStations[groundSurfaceProfileStations.length - 1].distance;
          if (totalDist) groundSurfaceProfile.drawProfileGraphics(totalDist);
        }

        // Draw terrace cross sections and water levels
        if (profiles.data.terraceProfileList) {
          this.terraceProfileData = profiles.data.terraceProfileList;
          this.drawUgoTerraceStructures();
        }
      }
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError(
          (error as any).response.data.errorMessage,
          [],
          (error as any).response.data.originalErrorCode
        );
      }
    } finally {
      this.setUpSketchCapability();
      this.raiseStartLengthyProcessEvent(false);
    }
  }

  private ugoInletSymbol = {
    type: 'picture-marker' as const,
    url: UgoInlet,
    width: 20,
    height: 20,
    xoffset: 0,
    yoffset: 0,
  };

  private ugoOutletSymbol = {
    type: 'picture-marker' as const,
    url: UgoOutlet,
    width: 20,
    height: 20,
    xoffset: 0,
    yoffset: 0,
  };

  private ugoJunctionSymbol = {
    type: 'picture-marker' as const,
    url: UgoJunction,
    width: 20,
    height: 20,
    xoffset: 0,
    yoffset: 0,
  };

  private ugoGradeBreakSymbol = {
    type: 'picture-marker' as const,
    url: UgoGradeBreak,
    width: 20,
    height: 20,
    xoffset: 0,
    yoffset: 0,
    angle: 270,
  };


  private async displayInletOutletAndJunctions(paths, type: 'inlets' | 'outlets' | 'junctions' | 'gradeBreaks') {
    const symbol =
      type === 'inlets'
        ? this.ugoInletSymbol
        : type === 'outlets'
          ? this.ugoOutletSymbol
          : type === 'junctions'
            ? this.ugoJunctionSymbol
            : this.ugoGradeBreakSymbol;
    const layer =
      type === 'inlets'
        ? this.ugoInletLayer
        : type === 'outlets'
          ? this.ugoOutletLayer
          : type === 'junctions'
            ? this.ugoJunctionLayer
            : this.ugoGradeBreakLayer;


    paths.forEach((point) => {
      const geom = new Point({
        x: point[0],
        y: point[1],
        spatialReference: this.spatialReference,
      });

      const graphic = new Graphic({
        geometry: geom,
        symbol: symbol as any,
      });

      layer?.add(graphic);
    });
    if (this._profileView) putStructureRepOnTop(this._profileView, layer.id);
  }

  protected get ugoInletLayer() {
    const id = 'ugo_inlet';
    const title = 'ugo inlet';

    if (!this._ugoInletLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._ugoInletLayer = layer as GraphicsLayer;
      } else {
        this._ugoInletLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._ugoInletLayer, 10);
      }
    }
    return this._ugoInletLayer;
  }

  protected get ugoOutletLayer() {
    const id = 'ugo_outlet';
    const title = 'ugo outlet';

    if (!this._ugoOutletLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._ugoOutletLayer = layer as GraphicsLayer;
      } else {
        this._ugoOutletLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._ugoOutletLayer, 10);
      }
    }
    return this._ugoOutletLayer;
  }

  protected get ugoJunctionLayer() {
    const id = 'ugo_gradebreak';
    const title = 'ugo GradeBreak';

    if (!this._ugoGradeBreakLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._ugoGradeBreakLayer = layer as GraphicsLayer;
      } else {
        this._ugoGradeBreakLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._ugoGradeBreakLayer, 10);
      }
    }
    return this._ugoGradeBreakLayer;
  }

  protected get ugoGradeBreakLayer() {
    const id = 'ugo_junction';
    const title = 'ugo junction';

    if (!this._ugoJunctionLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._ugoJunctionLayer = layer as GraphicsLayer;
      } else {
        this._ugoJunctionLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._ugoJunctionLayer, 10);
      }
    }
    return this._ugoJunctionLayer;
  }
  drawUgoTerraceStructures() {
    this.resetProfileStructures();
    const xStretchFactor = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
    const yStretchFactor = designStore.commonVerticalStretchFactor
      ? designStore.commonVerticalStretchFactor
      : AppConstants.profileStretchFactor;

    if (this.terraceProfileData?.length) {
      this.terraceProfileData.forEach((terrace) => {
        // Waterlevel
        if (terrace.waterLevelProfile?.stationDTOList?.length) {
          const path = [
            [
              terrace.waterLevelProfile.stationDTOList[0].distance * xStretchFactor,
              terrace.waterLevelProfile.stationDTOList[0].inputPoint.z * yStretchFactor,
            ],
            [
              terrace.waterLevelProfile.stationDTOList[terrace.waterLevelProfile.stationDTOList.length - 1].distance *
              xStretchFactor,
              terrace.waterLevelProfile.stationDTOList[terrace.waterLevelProfile.stationDTOList.length - 1].inputPoint
                .z * yStretchFactor,
            ],
          ];

          if (path.length) {
            const polyline = new Polyline({
              paths: [path],
              spatialReference: this.spatialReference,
            });

            const polylineGraphic = new Graphic({
              geometry: polyline,
              symbol: channelLineSymbol.waterLevelSymbol,
            });

            this.waterLevelLayer.add(polylineGraphic);
          }
        }
        // Cross section
        if (terrace.crossSectionProfile?.stationDTOList?.length) {
          const path: [number, number][] = [];
          terrace.crossSectionProfile.stationDTOList.forEach((station) => {
            path.push([station.distance * xStretchFactor, station.inputPoint.z * yStretchFactor]);
          });
          if (path.length) {
            const polyline = new Polyline({
              paths: [path],
              spatialReference: this.spatialReference,
            });
            const lineSymbol = reachLineSymbol.designLineSymbol;
            const polylineGraphic = new Graphic({
              geometry: polyline,
              symbol: lineSymbol,
            });

            this.crosssectionLayer.add(polylineGraphic);
          }
        }
      });
    }
  }

  async redrawUgoProfiles() {
    this.maxXmin = 10000000;
    this.maxXmax = -1000;
    this.maxYmin = 10000000;
    this.maxYmax = -1000;
    designStore.commonHorizontalStretchFactor = undefined;
    designStore.commonVerticalStretchFactor = undefined;

    this.profileKeys.forEach((keyCode) => {
      if (keyCode) {
        const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
        profile?.softProfileReset();
      }
    });

    this._centerlineStations = [...designStore.alignmentProfileStations];

    // Get ymax and ymin
    let yValues;
    if (this._centerlineStations) {
      yValues = await this.getMaxMinYValues([this._centerlineStations], this.alignmentCenterLine);

      // Centerline
      const keyCode = 10;
      const centerlineProfile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
      centerlineProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
      centerlineProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
      const totalDist =
        this._centerlineStations.length > 1
          ? this._centerlineStations[this._centerlineStations.length - 1].distance
          : 0;
      centerlineProfile.mapGroundProfilePoints(this._centerlineStations);
      if (totalDist) centerlineProfile.drawProfileGraphics(totalDist);

      // Terrace cross sections
      this.drawUgoTerraceStructures();

      // Pipeline
      await this.createChannelFromSavedUgo();
    }
  }

  resetProfileStructures() {
    this.waterLevelLayer.removeAll();
    this.crosssectionLayer.removeAll();
  }

  resetFixtureLayers() {
    this.ugoInletLayer.removeAll();
    this.ugoOutletLayer.removeAll();
    this.ugoJunctionLayer.removeAll();
    this.ugoGradeBreakLayer.removeAll();
  }

  async createChannelFromSavedUgo() {
    this.resetFixtureLayers();
    const stations: IUgoVertexParamsDTO[] = ugoDesignStore.ugoDesignObject.ugoDesignStations;

    const yStretchFactor = designStore.commonVerticalStretchFactor
      ? designStore.commonVerticalStretchFactor
      : AppConstants.profileStretchFactor;
    const xStretchFactor = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;

    if (ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId && stations.length) {
      const inlets: [number, number][] = [];
      const outlets: [number, number][] = [];
      const junctions: [number, number][] = [];
      const gradeBreaks: [number, number][] = [];
      const path: [number, number][] = [];
      stations.forEach((station: any, i: number) => {
        const fixtureTypeId = station.fixtureTypeId;

        /* Check fixture types and add to appropriate arrays */
        // Riser Inlet and Canopy Inlet
        if (fixtureTypeId === 6 || fixtureTypeId === 5) {
          inlets.push([parseStationString(station.endStation) * xStretchFactor, station.pipeFlowElev * yStretchFactor]);
        }
        // Standard Outlet and Vertical Outlet
        else if (fixtureTypeId === 7 || fixtureTypeId === 8) {
          outlets.push([parseStationString(station.endStation) * xStretchFactor, station.pipeFlowElev * yStretchFactor]);
        }
        // Junction and Tee
        else if (fixtureTypeId === 4 || fixtureTypeId === 3) {
          junctions.push([parseStationString(station.endStation) * xStretchFactor, station.pipeFlowElev * yStretchFactor]);
        }
        // Elbow and Grade Break
        else if (fixtureTypeId === 2 || fixtureTypeId === 1) {
          gradeBreaks.push([parseStationString(station.endStation) * xStretchFactor, station.pipeFlowElev * yStretchFactor]);
        }

        const stationValue = station.reachId === 'START' ? station.startStation : station.endStation;
        if (
          (i > 0 && i < stations.length - 1) ||
          (i === 0 && station.groundElev > 0) ||
          (i === stations.length - 1 && station.groundElev > 0)
        ) {
          path.push([parseStationString(stationValue) * xStretchFactor, station.pipeFlowElev * yStretchFactor]);
        }
      });
      await this.generateReachLineFromStations(path);
      await this.addCenterReachLabels(stations);
      await this.displayInletOutletAndJunctions(inlets, 'inlets');
      await this.displayInletOutletAndJunctions(outlets, 'outlets');
      await this.displayInletOutletAndJunctions(junctions, 'junctions');
      await this.displayInletOutletAndJunctions(gradeBreaks, 'gradeBreaks');
    }
  }

  async addReachVertex(info: any) {
    this.mainSketchVm?.complete();
    this.changePointSymbol('reachStation');
    if (this.sketchLayer && this.sketchLayer.graphics && this.sketchLayer.graphics.length > 0) {
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      const path = currentLine?.paths[0];
      // Create the new point

      const newPoint = new Point({
        x:
          parseFloat(info.item.endStation.replace('+', '')) *
          (designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1),
        y:
          parseFloat(info.item.channelElev) *
          (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor),
        spatialReference: currentLine.spatialReference,
      });

      if (path[0][0] > path[path.length - 1][0]) {
        //digitized right to left, correct the order of pointIndex
        info.index = path.length - info.index;
      }

      // Add the new point to the polyline at the specified index
      currentLine.insertPoint(0, info.index, newPoint);
      const newLine = new Polyline({
        paths: currentLine.paths,
        spatialReference: currentLine.spatialReference,
      });
      this.sketchLayer.graphics.getItemAt(0).geometry = newLine;
    }
    await this.createChannelFromSavedUgo();
    const reachStationsLayer = this._profileView?.map.findLayerById('deleteReachStations') as FeatureLayer;
    if (reachStationsLayer) {
      this.addPoint();
    }
  }

  reshapePolyline(propAddEdits?: any) {
    this.addUgoEdits = propAddEdits;
    this.generateReachStationLayer(true);
    if (this.mainSketchVm?.layer && this.mainSketchVm.layer.graphics && this.mainSketchVm.layer.graphics.length > 0) {
      this.mainSketchVm?.update([this.mainSketchVm.layer.graphics.getItemAt(0)], {
        tool: 'reshape',
        enableRotation: false,
        enableScaling: false,
        preserveAspectRatio: false,
        toggleToolOnClick: false,
      });
      this.isAfterReshapeVertexAdd = false;
    }
  }

  public async updateUgoTableOnChangeStationValue(dataPoint: XyzPoint, index: number): Promise<void> {
    const stations = ugoDesignStore.ugoDesignObject.ugoDesignStations;

    // Update status
    stations[index].status = 'IN PROGRESS';

    // Update endStation
    stations[index].endStation = formatStationString(dataPoint.getX().toFixed(1));

    // Update pipeFlowElev
    stations[index].pipeFlowElev = parseFloat(dataPoint.getY().toFixed(1));

    // Update startStation for the next station if it exists
    if (index + 1 < stations.length) {
      stations[index + 1].startStation = formatStationString(dataPoint.getX().toFixed(1));
    }

    // Prepare payload for fetching ground elevation
    const payload = {
      alignedElementDesignParametersId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
      reachStationDistance: parseFloat(dataPoint.getX().toFixed(1)),
    };
    const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);

    // Update ground elevation and cutFill
    if (response.data && response.data.groundElevation !== undefined) {
      stations[index].groundElev = parseFloat(response.data.groundElevation.toFixed(1));
    }

    // Recalculate grades and distances for the current and adjacent stations
    const currentStation = stations[index];
    if (index > 0) {
      const prevStation = stations[index - 1];
      const deltaCurY = prevStation?.pipeFlowElev - currentStation.pipeFlowElev;
      const deltaCurX = parseStationString(prevStation?.endStation) - parseStationString(currentStation.endStation);
      if (deltaCurX !== 0) {
        currentStation.grade = deltaCurY / deltaCurX;
        currentStation.stationDist = Math.abs(deltaCurX);
      }
    }
    if (index + 1 < stations.length) {
      const nextStation = stations[index + 1];
      const deltaNextY = nextStation?.pipeFlowElev - currentStation.pipeFlowElev;
      const deltaNextX = parseStationString(nextStation?.endStation) - parseStationString(currentStation.endStation);
      if (deltaNextX !== 0) {
        nextStation.grade = deltaNextY / deltaNextX;
        nextStation.stationDist = Math.abs(deltaNextX);
      }
    }
    // Persist the updated stations back to the store
    ugoDesignStore.ugoDesignObject.ugoDesignStations = JSON.parse(JSON.stringify(stations));
  }

  startProfileEditTool(openFixtureModal) {
    this.stopUgoSketch();
    if (this._profileView) {
      this.setUpMultipoint();

      if (this.channelIndexes.length > 0) {
        // Only add listeners if there are editable stations
        const element = document.getElementById('ugo-Profile');
        if (element) element.style.cursor = 'pointer';

        // Add listeners
        document.addEventListener('keydown', this.handleUgoEscKey);
        this.ugoProfileMoveHandler = this._profileView.on('pointer-move', (event) => this.handleProfileMove(event));
        this.ugoProfileClickHandler = this._profileView.on('pointer-down', (event) => {
          const point = this._profileView!.toMap(event);
          this.handleUgoProfileClick(point, openFixtureModal);
        });
      }
    }
  }

  private handleUgoProfileClick(point: Point, openFixtureModal) {
    const findResult = esriGeometryEngine.nearestVertex(this.channelStationMP, point);
    if (findResult) {
      const x = findResult.coordinate.x;
      const y = findResult.coordinate.y;
      const index = this.channelIndexes.find((obj) => obj.point.x === x && obj.point.y === y).index;
      const station = ugoDesignStore.ugoDesignObject.ugoDesignStations[index];
      openFixtureModal({}, station, index);
    }
    this.stopUgoSketch();
  }

  private setUpMultipoint() {
    if (this._profileView) {
      const multipoint: Multipoint = new Multipoint({ spatialReference: this._profileView.spatialReference });
      const length = ugoDesignStore.ugoDesignObject.ugoDesignStations.length;
      for (let i = 0; i < length; i++) {
        const station = ugoDesignStore.ugoDesignObject.ugoDesignStations[i];
        // if (!station.fixtureDtoJson) {  // Don't allow adding fixture where existing fixture is
        const formattedStation = parseFloat(station.endStation.replace('+', ''));
        const point = new Point({
          x: formattedStation * designStore.commonHorizontalStretchFactor!,
          y: station.pipeFlowElev * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor),
          spatialReference: this._profileView.spatialReference,
        });
        multipoint.addPoint(point);
        this.channelIndexes.push({ point, index: i });
        // }
      }
      this.channelStationMP = multipoint;
      const graphic = new Graphic({ geometry: multipoint, symbol: reachStationSymbol.editableStationSymbol });
      const lyr = new GraphicsLayer({
        graphics: [graphic],
        id: 'editableStations',
      });
      this._profileView.map.addMany([lyr, this.feedbackLayer]);
      this._profileView.map.add(this.feedbackLayer);
    }
  }

  private handleUgoEscKey = (event) => {
    if (event.key === 'Escape') {
      this.stopUgoSketch();
    }
  };

  private stopUgoSketch() {
    const element = document.getElementById('ugo-Profile');
    if (element) element.style.cursor = 'default';
    this.channelIndexes = [];
    this.feedbackLayer.removeAll();
    if (this._profileView) {
      const editStationsLyr = this._profileView.map.findLayerById('editableStations');
      this._profileView.map.removeMany([editStationsLyr, this.feedbackLayer]);
    }
    this.ugoProfileClickHandler?.remove();
    this.ugoProfileClickHandler = undefined;
    this.ugoProfileMoveHandler?.remove();
    this.ugoProfileMoveHandler = undefined;
    document.removeEventListener('keydown', this.handleUgoEscKey);
  }

  async stopEditing() {
    this.clearDelete();
    this.stopUgoSketch();
    this.mainSketchVm?.complete();
    await this.createChannelFromSavedUgo();
  }

  cleanUp() { }

  Dispose() {
    this.cleanUp();
    super.Dispose();
    this.mainSketchVm?.destroy();
  }
}

export { DynamicUGOProfileStore };
